import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import {
  captureTokenFromUrl,
  trySilentAuth,
  redirectToMidway,
  isAuthenticated,
  getCurrentUser,
  getToken,
  logout,
  type MidwayUser,
} from './midway';

interface AuthContextType {
  user: MidwayUser | null;
  token: string | null;
  loading: boolean;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: true,
  signIn: () => {},
  signOut: () => {},
});

export function useAuth(): AuthContextType {
  return useContext(AuthContext);
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * WM-2: AuthProvider wraps the app and handles Midway authentication state.
 *
 * On mount:
 * 1. Check if a token was returned in the URL (post-redirect)
 * 2. If not, try silent auth (existing Midway session)
 * 3. If not authenticated, redirect to Midway login
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<MidwayUser | null>(null);

  useEffect(() => {
    async function initAuth() {
      // Step 1: Check if token came back in the URL
      if (captureTokenFromUrl()) {
        setUser(getCurrentUser());
        setLoading(false);
        return;
      }

      // Step 2: Already authenticated (e.g., token in memory from before)
      if (isAuthenticated()) {
        setUser(getCurrentUser());
        setLoading(false);
        return;
      }

      // Step 3: Try silent auth (use existing Midway cookies)
      const silentOk = await trySilentAuth();
      if (silentOk) {
        setUser(getCurrentUser());
        setLoading(false);
        return;
      }

      // Step 4: Not authenticated — redirect to Midway
      redirectToMidway();
    }

    initAuth();
  }, []);

  const value: AuthContextType = {
    user,
    token: getToken(),
    loading,
    signIn: redirectToMidway,
    signOut: logout,
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: '16px',
        }}
      >
        <img src="/mailhub-icon.svg" alt="MailHub" style={{ width: '64px', height: '64px' }} />
        <div style={{ fontSize: '18px', fontWeight: '500', color: '#0F1B2D' }}>MailHub</div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>Authenticating with Midway…</div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
